/**
 * Otomatik Mail Raporlari - Sirket Ana Sayfasi.
 *
 * Bu sayfa "Reports.tsx" (gorev raporlari) ile karistirilmamalidir;
 * burasi ozellikle otomatik gunluk/haftalik/aylik MAIL raporlarinin
 * (gun sonu rapor mailleri) yonetildigi sayfadir.
 *
 * Erisim kontrolu (F5):
 *  - Mount edildiginde /api/SuperAdminMailReports/access-status sorgulanir.
 *  - MailReportsEnabled=false ise tum sekmeler disabled, buyuk bir uyari gosterilir.
 *  - SuperAdmin kullanicilar icin bu kontrol bypass edilir.
 *
 * Sekme yapisi:
 *   1) Zamanlamalar
 *   2) Calisma Loglari
 *   3) Sablon/Branding (kendi sirketi icin)
 *   4) SMTP - SuperAdmin icin yonetim, sirket kullanicisi icin SADECE OKUNUR view
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import PaletteIcon from '@mui/icons-material/Palette';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useAuth } from '../../../contexts/AuthContext';
import { BRAND } from '../../../config/brand';
import { getAccessStatus } from './services/mailReportsApi';
import SchedulesTab from './tabs/SchedulesTab';
import RunLogsTab from './tabs/RunLogsTab';
import TemplateSettingsTab from './tabs/TemplateSettingsTab';
import SmtpSettingsTab from './tabs/SmtpSettingsTab';
import CompanySmtpReadOnlyView from './tabs/CompanySmtpReadOnlyView';
import ReportsHubBackButton from '../../reports/hub/ReportsHubBackButton';
import GuideButton from '../../common/guide/GuideButton';

type TabId = 'schedules' | 'logs' | 'template' | 'smtp';

interface TabPanelProps {
  current: TabId;
  value: TabId;
  children: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({ current, value, children }) => (
  <div hidden={current !== value} role="tabpanel" style={{ width: '100%' }}>
    {current === value && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const MailReports: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const [tab, setTab] = React.useState<TabId>('schedules');

  // Erisim flag'i: null = henuz yuklenmedi, true/false = sonuc
  const [accessAllowed, setAccessAllowed] = React.useState<boolean | null>(null);
  const [accessError, setAccessError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // SuperAdmin icin flag her zaman aciktir
    if (isSuperAdmin) {
      setAccessAllowed(true);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const status = await getAccessStatus();
        if (!cancel) setAccessAllowed(status?.MailReportsEnabled ?? true);
      } catch (err: any) {
        if (!cancel) {
          // Backend offline veya benzeri durumda fail-open: kullaniciyi engellemekten kacin
          setAccessError(err?.response?.data?.Message || err?.message || 'Erisim durumu sorgulanamadi');
          setAccessAllowed(true);
        }
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isSuperAdmin]);

  if (accessAllowed === false) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: 3,
            background: 'linear-gradient(180deg,#fff5f5 0%,#ffffff 100%)',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(239,68,68,0.08)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <BlockIcon sx={{ fontSize: 44, color: 'error.main' }} />
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Modül Devre Dışı
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Otomatik Mail Raporları modülü, şirketiniz için sistem yöneticisi
            tarafından devre dışı bırakılmıştır.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Yeni zamanlama oluşturma, mevcut zamanlamaları güncelleme ve test mail gönderme işlemleri
            şu anda kullanılamaz. Modül aktivasyonu için lütfen{' '}
            <strong>{BRAND.contact.email}</strong> ile iletişime geçin.
          </Typography>
          <Button variant="contained" color="error" href={`mailto:${BRAND.contact.email}`}>
            {BRAND.companyName} Destek ile İletişime Geç
          </Button>
        </Paper>
      </Container>
    );
  }

  if (accessAllowed === null) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Erişim durumu sorgulanıyor...
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 2,
          color: 'common.white',
          background:
            'linear-gradient(135deg,#1e1b4b 0%,#4f46e5 55%,#7c3aed 100%)',
          boxShadow: '0 6px 24px -8px rgba(79,70,229,0.45)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <ReportsHubBackButton reportPath="/accounting/mail-raporlari" />
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MarkEmailReadIcon sx={{ fontSize: 32, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>
                Otomatik Mail Raporları
              </Typography>
              <GuideButton guideKey="documents.mailReports" color="inherit" size="small" />
              {isSuperAdmin && (
                <Chip
                  size="small"
                  label="SuperAdmin"
                  icon={<VerifiedIcon sx={{ color: '#fff !important' }} />}
                  sx={{
                    background: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', maxWidth: 720 }}>
              Günlük, haftalık ve aylık gün sonu raporlarının otomatik mail olarak gönderilmesini buradan yönetin.
              Şablonu özelleştirebilir, çalışma geçmişini izleyebilir ve test mail atabilirsiniz.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {accessError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {accessError} — Modül şu anda açık varsayılarak gösteriliyor; işlemler sırasında 403 alabilirsiniz.
        </Alert>
      )}

      <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
            },
          }}
        >
          <Tab icon={<ScheduleIcon fontSize="small" />} iconPosition="start" label="Zamanlamalar" value="schedules" />
          <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="Çalışma Logları" value="logs" />
          <Tab icon={<PaletteIcon fontSize="small" />} iconPosition="start" label="Şablon / Branding" value="template" />
          <Tab icon={<LockIcon fontSize="small" />} iconPosition="start" label="SMTP" value="smtp" />
        </Tabs>

        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TabPanel current={tab} value="schedules">
            <SchedulesTab />
          </TabPanel>
          <TabPanel current={tab} value="logs">
            <RunLogsTab />
          </TabPanel>
          <TabPanel current={tab} value="template">
            <TemplateSettingsTab />
          </TabPanel>
          <TabPanel current={tab} value="smtp">
            {isSuperAdmin ? <SmtpSettingsTab /> : <CompanySmtpReadOnlyView />}
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default MailReports;
